/**
 * Charges fixes saisies depuis le dashboard — contrat de `/api/charges`.
 *
 * Même règle que les autres fichiers de `shared/types` : importé par le back
 * et le front, jamais redéclaré. Les listes doivent rester alignées sur les
 * CHECK de la table `fixed_charges` (migration 019).
 */

import type { PeriodRange } from './stats';

export const CHARGE_CATEGORIES = ['salaries', 'rent', 'software', 'services', 'marketing', 'other'] as const;
export type ChargeCategory = (typeof CHARGE_CATEGORIES)[number];

export const CHARGE_CATEGORY_LABELS: Record<ChargeCategory, string> = {
  salaries: 'Salaires',
  rent: 'Loyer & bureau',
  software: 'Logiciels & abonnements',
  services: 'Prestataires',
  marketing: 'Marketing (hors pub)',
  other: 'Autres',
};

export const CHARGE_FREQUENCIES = ['monthly', 'quarterly', 'yearly', 'once'] as const;
export type ChargeFrequency = (typeof CHARGE_FREQUENCIES)[number];

export const CHARGE_FREQUENCY_LABELS: Record<ChargeFrequency, string> = {
  monthly: 'par mois',
  quarterly: 'par trimestre',
  yearly: 'par an',
  once: 'ponctuelle',
};

export const CHARGE_LABEL_MAX_LENGTH = 80;
export const CHARGE_NOTE_MAX_LENGTH = 500;
export const CHARGE_AMOUNT_MAX = 10_000_000;
/** Mois du tableau de tendance. */
export const CHARGES_TREND_MONTHS = 12;

export interface FixedCharge {
  id: number;
  label: string;
  category: ChargeCategory;
  /** En € HT, par échéance. */
  amount: number;
  frequency: ChargeFrequency;
  /** `YYYY-MM-DD` — première échéance (le jour, pour une charge ponctuelle). */
  startDate: string;
  /** `YYYY-MM-DD` inclus, ou `null` : toujours en cours. */
  endDate: string | null;
  note: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FixedChargeInput {
  label: string;
  category: ChargeCategory;
  amount: number;
  frequency: ChargeFrequency;
  startDate: string;
  endDate?: string | null;
  note?: string | null;
}

/** Charges d'une fenêtre, au prorata des jours couverts. */
export interface ChargesTotals {
  total: number;
  byCategory: Record<ChargeCategory, number>;
}

export interface ChargesWindow {
  from: string;
  to: string;
  totals: ChargesTotals;
}

export interface ChargesStats {
  range: PeriodRange;
  comparison: { current: ChargesWindow; previous: ChargesWindow; yearAgo: ChargesWindow; comparisonsOverlap: boolean };
  /** Les `CHARGES_TREND_MONTHS` mois qui finissent avec la période, mois entiers. */
  months: Array<{ month: string } & ChargesTotals>;
  /** Toutes les charges saisies, pour le tableau de saisie. */
  charges: FixedCharge[];
}

export interface ChargesStatsResponse {
  stats: ChargesStats;
}

export interface FixedChargeResponse {
  charge: FixedCharge;
}
